import {
  useGetApiAdminIntacctCompanyInfo,
  usePostApiAdminProvisionTest,
} from '@sit/client-shared';
import { skipToken } from '@tanstack/react-query';
import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button';
import Card, {
  CardColumn,
  CardFooter,
  CardRow,
} from 'carbon-react/lib/components/card';
import Form from 'carbon-react/lib/components/form';
import Heading from 'carbon-react/lib/components/heading';
import Loader from 'carbon-react/lib/components/loader';
import Textbox from 'carbon-react/lib/components/textbox';
import { type FormEventHandler, useState } from 'react';
import GoBackButton from '../../components/GoBackButton';
import MissingPermissions from '../MissingPermissions/MissingPermissions';

const ProvisionTest = () => {
  const [companyId, setCompanyId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [userId, setUserId] = useState('');
  const [customIntacctEndpoint, setCustomIntacctEndpoint] = useState('');
  const [inputErrors, setInputErrors] = useState<Record<string, boolean>>({});
  const {
    mutate: checkPermissions,
    isPending,
    data,
  } = usePostApiAdminProvisionTest();
  const { data: intacctCompanyInfo } = useGetApiAdminIntacctCompanyInfo(
    companyId
      ? {
          pathParams: {
            intacctCompanyId: companyId,
          },
        }
      : skipToken,
  );

  const handleSubmit: FormEventHandler<Element> = (e) => {
    e.preventDefault();
    if (isPending || !companyId) return;
    checkPermissions({
      body: {
        companyId,
        password,
        userId,
        customIntacctEndpoint: customIntacctEndpoint || undefined,
      },
      queryParams: {
        keepAlive: 'true',
      },
    });
  };

  const validateInput = (key: string, value: string) => {
    const _errs = { ...inputErrors };
    if (!value) {
      _errs[key] = true;
    } else {
      delete _errs[key];
    }
    setInputErrors(_errs);
  };

  return (
    <div className="provision-container">
      <GoBackButton />
      <div className="provision-content">
        <div className="provision">
          <Card
            footer={
              <CardFooter>
                {!!data?.missingPermissions && (
                  <Box
                    display="flex"
                    justifyContent="center"
                    flexDirection="column"
                    p={2}
                    backgroundColor="white"
                  >
                    {data.missingPermissions.length === 0 ? (
                      <div>The WSU has all required permissions</div>
                    ) : (
                      <MissingPermissions
                        missing={data.missingPermissions}
                        companyTreeSettingsId={
                          intacctCompanyInfo?.companyTreeSettingsId
                        }
                      />
                    )}
                  </Box>
                )}
              </CardFooter>
            }
          >
            <Form
              errorCount={Object.keys(inputErrors).length}
              paddingBottom={2}
              saveButton={
                <Button
                  disabled={Object.keys(inputErrors).length > 0}
                  buttonType="primary"
                  key="verify"
                  type="submit"
                >
                  {isPending ? <Loader isInsideButton /> : 'Verify'}
                </Button>
              }
              onSubmit={handleSubmit}
            >
              <CardRow>
                <CardColumn align="left">
                  <Heading title="Enter Web Services User credentials" mt={2} />
                </CardColumn>
              </CardRow>
              <Textbox
                required
                error={
                  inputErrors.companyId ? 'This field is required' : undefined
                }
                label="Company ID"
                labelInline={false}
                labelAlign="left"
                name="companyId"
                onBlur={() => validateInput('companyId', companyId)}
                onChange={(e) => setCompanyId(e.target.value)}
                // @ts-expect-error -- carbon-react doesn't use type but it exists
                type="text"
                value={companyId}
              />
              <Textbox
                required
                error={
                  inputErrors.userId ? 'This field is required' : undefined
                }
                label="Web Services User ID"
                labelInline={false}
                labelAlign="left"
                name="userId"
                onBlur={() => validateInput('userId', userId)}
                onChange={(e) => setUserId(e.target.value)}
                // @ts-expect-error -- carbon-react doesn't use type but it exists
                type="text"
                value={userId}
              />
              <Textbox
                required
                error={
                  inputErrors.password ? 'This field is required' : undefined
                }
                label="Web Services User password"
                labelInline={false}
                labelAlign="left"
                name="password"
                onBlur={() => validateInput('password', password)}
                onChange={(e) => setPassword(e.target.value)}
                // @ts-expect-error -- carbon-react doesn't use type but it exists
                type={showPassword ? 'text' : 'password'}
                value={password}
              >
                <Button
                  buttonType="tertiary"
                  onClick={() => setShowPassword((s) => !s)}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </Button>
              </Textbox>
              <Textbox
                label="Custom Intacct endpoint"
                labelInline={false}
                labelAlign="left"
                name="customIntacctEndpoint"
                placeholder="https://api.intacct.com/ia/xml/xmlgw.phtml"
                onChange={(e) => setCustomIntacctEndpoint(e.target.value)}
                // @ts-expect-error typing broken for carbon-react, remove this once they fix it
                type="text"
                value={customIntacctEndpoint}
              />
            </Form>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProvisionTest;
